/// <reference types="vite/client" />
import {
  AddressVerificationApi,
  ApplicationStatusApi,
  BackendProductsApi,
  BureauApi,
  Configuration,
  DealerInfoApi,
  FraudVerdictApi,
  OpenLoanCheckApi,
  PayoffApi,
  ProgramRoleCapabilitiesApi,
  VendorSearchApi,
  VLALockApi,
  WorkbenchAnalystNotesApi,
  WorkbenchDashboardV2Api,
  WorkbenchDocsApi,
  WorkbenchDocsMessagesApi,
  WorkbenchDSCNotesApi,
  WorkbenchExitApi,
  WorkbenchExitOptionsApi,
  WorkbenchIncomeVerificationApi,
  WorkbenchInternalAggregatorValidationApi,
  WorkbenchInternalStatusHistoryApi,
  WorkbenchStipulationsApi,
  WorkbenchViewAllNotesApi,
  WorkbenchWorknumberApi,
  ZipCodeLookupApi,
} from "@credit-acceptance/step6-bff-client";

import { wrapBFFClientWithAuth } from "../lib/auth/authInterceptor";

// Fail loudly at module load if the BFF base URL is missing — otherwise
// requests silently fall through to the wrong path (e.g. `/stipulations`
// at the page-hosting origin) and surface as a confusing 404.
export const BFF_BASE_URL = import.meta.env.VITE_STEP6_BFF_BASE_URL;
if (!BFF_BASE_URL) {
  throw new Error(
    "VITE_STEP6_BFF_BASE_URL is not set. Check the env file for the current mode (.env, .env.test, .env.qa, …)."
  );
}

const config = new Configuration({
  basePath: BFF_BASE_URL,
  // Send the oauth2-proxy session cookie on every BFF request.
  baseOptions: { withCredentials: true },
});

export const backendProductsApi = wrapBFFClientWithAuth(new BackendProductsApi(config));

export const vendorSearchApi = wrapBFFClientWithAuth(new VendorSearchApi(config));

// DSC Workbench dashboard aggregation + Dashboard-shaped save (v2).
// `getWorkbenchV2(approvalId)` hits GET
// /api/v2/workbench/applications/{approvalId}/Dashboard and returns a
// DscWorkbenchDashboardResponseV2 — the same sections as v1 plus each
// applicant's current-state validations and the current stipulation panel
// (see useDscWorkbench).
// `saveDashboard(approvalId, dashboardSaveV2Request, idempotencyKey)` hits PUT
// /api/v2/workbench/applications/{approvalId}/Dashboard — same path as the
// GET above, different verb. Writable legs are driver / secondSigner
// (either or both); the remaining dashboard-shaped sections on the request
// are accepted for round-trip
// convenience but ignored. The response carries the save outcome AND a
// freshly refreshed full Dashboard snapshot, so a successful save no longer
// needs a separate GET to refresh the cache (see useSaveAllSections).
export const dscWorkbenchV2Api = wrapBFFClientWithAuth(new WorkbenchDashboardV2Api(config));

// VLA In-Use (deal) lock — pessimistic single-holder lock backing OR-20985.
// All three verbs hit /api/v1/workbench/applications/{approvalId}/vlas-in-use:
//   acquireLock (PUT)  -> 200 with VlaLockStatusResponse, or 409 if held by another user
//   getLockStatus(GET) -> 200 with { isVlaLocked, lockedByUserId, lockedByUserName, fullName }
//     (lockedByUserName is always null per the generated type; fullName is the
//     populated holder display name)
//   releaseLock (DELETE) -> 200 (idempotent; 404 only if the VLA itself is missing)
export const vlaLockApi = wrapBFFClientWithAuth(new VLALockApi(config));

// Application status + phase. `getApplicationStatus(approvalId)` hits
// GET /api/v1/workbench/applications/{approvalId}/status and returns an
// ApplicationStatusResponse ({ statusDescription, phaseDescription }), proxied
// by the BFF to the Credit Application Service.
export const applicationStatusApi = wrapBFFClientWithAuth(new ApplicationStatusApi(config));

// Workbench exit screen options. `getNextStatusPhases(approvalId, currentStatus,
// roleName)` hits GET /api/v1/workbench/{approvalId}/next-status-phases and
// returns a NextStatusPhaseResponse ({ currentStatus, nextStatusList,
// currentPhase, phasesList, manualPhase }). The BFF forwards to recap-service to
// produce the next-status / phase dropdown options shown when an analyst exits
// the account.
export const workbenchExitOptionsApi = wrapBFFClientWithAuth(new WorkbenchExitOptionsApi(config));

// Internal-tab Aggregator Validation (OR-24019). `getAggregatorValidation(approvalId)`
// hits GET /api/v1/workbench/applications/{approvalId}/aggregator/validation and
// returns an AggregatorValidationResponseView ({ aggregatorPartnerName, aggregatorDeal,
// hasFailedValidations, validations: AggregatorValidationItemView[], history?:
// AggregatorValidationHistoryEntryView[] }), forwarded by the BFF to recap-service.
// Non-aggregator deals come back 200 with an empty validations list (never 404);
// history/lastUpdatedDatetime are populated via the step6-bff pass-through
// shipped in OR-24019. caps2 step-6 surfaced this as an Internal-tab popup; the
// Workbench renders it as a tab inside the internal-audit drawer.
export const workbenchInternalAggregatorValidationApi = wrapBFFClientWithAuth(
  new WorkbenchInternalAggregatorValidationApi(config)
);

// Workbench exit — move account. `moveAccount(approvalId, { targetStatus,
// targetPhase, currentStatus, currentPhase }, idempotencyKey?)` hits POST
// /api/v1/workbench/{approvalId}/move-account and returns a
// MoveAccountResponse. The BFF forwards to recap-service to run the full
// Save & Exit flow: status/phase transition, work-queue closure,
// vehicle-sold-in-CAPS flip, lender notifications, car-lot attention flag,
// and lock release.
export const workbenchExitApi = wrapBFFClientWithAuth(new WorkbenchExitApi(config));

// Aggregated documents + messages. `getDocsMessages(appId)` hits
// GET /api/v1/workbench/applications/{appId}/docs-messages and returns a
// composite of appxtender/piston docs and messages. The generated client types
// the response as `object`; the query hook casts it to `DocumentsAndMessagesResponse`
// (see features/documents/api/types.ts). The app passes the route `approvalId`
// as `appId`, matching the sibling `applications/{…}` status + VLA-lock reads.
export const workbenchDocumentsAndMessagesApi = wrapBFFClientWithAuth(new WorkbenchDocsMessagesApi(config));

// DSC Pre-approval Change History (OR-21039). `getDscNotes(approvalId, phase)`
// hits GET /api/v1/workbench/{approvalId}/dsc-notes?phase=all and returns an
// array of DscNoteResponse change-history rows (date, attributeName, oldValue,
// newValue, …), forwarded by the BFF from recap-service. Empty histories come
// back as 200 with an empty array (never 404). caps2 step-6 surfaces this as a
// popup; the Workbench renders it as an internal-audit drawer tab.
export const workbenchDscNotesApi = wrapBFFClientWithAuth(new WorkbenchDSCNotesApi(config));

// Analyst notes (OR-22625). Both verbs hit
// /api/v1/workbench/applications/{approvalId}/analyst-notes, forwarded by the
// BFF to recap-service:
//   getAnalystNotes(approvalId)         (GET)  -> AnalystNoteResponse[] ({ createdDatetime,
//     authorUserId, note }); an empty history comes back as 200 with an empty array (never 404).
//   saveAnalystNote(approvalId, body,   (POST) -> 204 No Content; recap derives the author +
//     idempotencyKey?)                          timestamp server-side. An optional Idempotency-Key
//                                               dedups accidental double-submits.
// caps2 step-6 surfaced these as a popup; the Workbench renders them in the
// amber Analyst Notes FAB modal.
export const workbenchAnalystNotesApi = wrapBFFClientWithAuth(new WorkbenchAnalystNotesApi(config));

// Analyst document uploads (Send Proofs). `generateUploadUrl(appId, { category,
// filename, dealerId })` hits PUT /api/v1/workbench/applications/{appId}/docs/upload-url
// and returns { uploadUrl, proofFileId }. The app then PUTs the raw file bytes
// directly to the presigned `uploadUrl` (see features/documents/api/uploadProof.ts)
// — that S3 request deliberately bypasses this authenticated client.
// `applyDocAction(appId, docId, { checkIn?, moveToApprovalNumber?, moveToState? })`
// hits POST /api/v1/workbench/applications/{appId}/docs/{docId}/actions (see
// features/documents/api/useApplyDocumentAction.ts).
export const workbenchDocumentsApi = wrapBFFClientWithAuth(new WorkbenchDocsApi(config));

// Work Number (purchased employer info) — per-applicant income verification,
// forwarded by the BFF to recap-service. All four verbs hit
// /api/v1/workbench/{approvalId}/applicants/{applicantId}/worknumber:
//   getEligibility     (GET .../eligibility)    -> object: the applicant's eligibility snapshot
//   getHistory         (GET .../history)        -> object: previously-pulled verification history
//   pullWorkNumber     (POST)                   -> object: freshly-pulled history (dedup via Idempotency-Key);
//     also fires a purchased-employer-data recalc. A deferred/failed recalc never fails the pull —
//     it surfaces as X-Recalc-Deferred / X-Recalc-Deferred-Reason response headers.
//   retryWorkNumberRecalc (POST .../recalc-retry) -> RecalcResponse: re-invokes the recalc alone
//     (no vendor call). `warnings` is non-empty when the recalc is still deferred/failed.
// The generated client types most responses as `object`; the query hooks cast to
// the hand-written view models in features/review/sections/Signers/api/worknumber.types.ts.
export const workbenchWorknumberApi = wrapBFFClientWithAuth(new WorkbenchWorknumberApi(config));

// Call Script (OR-21767) — per-applicant employment-verification call script,
// forwarded by the BFF to recap-service. All three verbs hit
// /api/v1/workbench/{approvalId}/applicants/{applicantId}/income-verification/call-script[/history],
// plus a global questions lookup:
//   getHistory1             (GET)  -> object: the applicant's saved Call Script history
//   saveCallScript          (POST) -> object: the freshly created history entry
//   getVerificationQuestions(GET .../income-verification/questions?type=) -> VerificationQuestionView[]
// The generated client types the history/save responses as `object`; the query
// hooks cast to the hand-written view models in
// features/review/sections/Signers/api/callScript.types.ts.
export const workbenchIncomeVerificationApi = wrapBFFClientWithAuth(new WorkbenchIncomeVerificationApi(config));

// Stipulations panel view (OR-22754). `getStipulations(approvalId, openOnly?)`
// hits GET /api/v1/workbench/applications/{approvalId}/stipulations and returns a
// StipulationListResponse, forwarded verbatim by the BFF from stipulation-service.
// `updatePorException` for OR-20983 is auto-generated (W6 endpoint).
export const workbenchStipulationsApi = wrapBFFClientWithAuth(new WorkbenchStipulationsApi(config));

// Internal-tab View All Notes (OR-22848). `getViewAllNotes(approvalId, filter?)`
// hits GET /api/v1/workbench/applications/{approvalId}/view-all-notes and returns
// a ViewAllNotesResponseView ({ filter, notes: ViewAllNotesEntryView[] }),
// forwarded by the BFF to recap-service. `filter` is opaque to the BFF —
// recap is the single source of truth for the accepted values (caps2 parity:
// All, CAPS, DEALER, DSC Analyst, Re-assign, Verification, Advance Adjustment).
// caps2 step-6 surfaced this as the Internal tab; the Workbench renders it as a
// tab inside the internal-audit drawer.
export const workbenchViewAllNotesApi = wrapBFFClientWithAuth(new WorkbenchViewAllNotesApi(config));

// Internal-tab Status History (OR-22868). `getInternalStatusHistory(approvalId)`
// hits GET /api/v1/workbench/applications/{approvalId}/internal-status-history
// and returns an InternalStatusHistoryResponseView ({ currentStatus, currentPhase,
// history: InternalStatusHistoryEntryView[] }), forwarded by the BFF to recap-service.
// caps2 step-6 surfaced this as the Internal tab; the Workbench renders it as a
// tab inside the internal-audit drawer.
export const workbenchInternalStatusHistoryApi = wrapBFFClientWithAuth(new WorkbenchInternalStatusHistoryApi(config));

// ZIP -> city/state lookup. `lookupZipCode(zip)` hits
// GET /api/v1/workbench/addresses/fromzip/{zip} and returns a ZipCodeResponse
// ({ zip, city, state }), proxied by the BFF to cast-facade-service.
export const zipLookupApi = wrapBFFClientWithAuth(new ZipCodeLookupApi(config));

// USPS address verification/standardization (address-standardization-spec.md).
// `verifyAddress(addressVerificationRequest)` hits POST
// /api/v1/workbench/addresses/verify and returns an AddressVerificationResponse
// (standardized address fields + `status`), proxied by the BFF to
// cast-facade-service. Driven by the Signers form's per-slot verification gate
// (see features/review/sections/Signers/useAddressVerificationGate.ts).
export const addressVerificationApi = wrapBFFClientWithAuth(new AddressVerificationApi(config));

// View Bureau (OR-23155). `getBureauView(approvalId, applicantId)` hits
// GET /api/v1/workbench/applications/{approvalId}/applicants/{applicantId}/bureau
// and returns the bureau aggregate for ONE applicant (CAPS credit score,
// frozen flag, single bureau report — code/name/text), enriched with the
// sales person who sold the deal. The generated client types the response as
// `object`; the query hook casts it to `BureauViewResponse` (see
// features/viewBureau/api/types.ts) — hand-written directly off a real
// response since no OpenAPI schema exists yet for this endpoint. Called once
// per applicant (driver, and secondSigner when present) and merged into a
// single modal view model.
export const workbenchBureauApi = wrapBFFClientWithAuth(new BureauApi(config));

// Dealer Information popup (SVRS-56279). `getDealerInfo(approvalId)` hits
// GET /api/v1/workbench/applications/{approvalId}/dealer-info and returns a
// DealerInfoResponse: `dealerProfile` (fatal — always present on 200),
// `otherContacts` (MAM/DOS) and `salesPerson`, each individually omitted when
// its own dealer-management lookup fails. The Volume / Avg Days to Fund /
// contracts-in-AH block from the legacy popup has no producer today
// (SVRS-41708) and is not part of this response.
export const dealerInfoApi = wrapBFFClientWithAuth(new DealerInfoApi(config));

// Open-loan check (OR-23780). `checkOpenLoans({ driver, cosigner?, dealerId,
// creditApplicationReceiptDate })` hits POST /api/v1/workbench/openloans/check
// and returns an OpenLoanCheckResponse, proxied by the BFF to cast-facade-service.
// Each open-loan entry is enriched by the BFF with dealerName and disclosureMessage.
export const openLoanCheckApi = wrapBFFClientWithAuth(new OpenLoanCheckApi(config));

// Payoff/settlement letter generation (OR-23781). `generatePayoffLetter`/
// `generateSettlementLetter` hit POST /api/v1/workbench/payoffs/payoffletter
// and /api/v1/workbench/payoffs/settlementletter, each returning a
// PayoffLetterResponse ({ documentId }), proxied by the BFF to
// cast-facade-service. Fetching the generated PDF content (getPayoffLetterContent/
// getSettlementLetterContent) is a separate story.
export const payoffApi = wrapBFFClientWithAuth(new PayoffApi(config));

// Program role capabilities (Step6 role-based access). `getRolePermissions(roleName)`
// hits GET /api/v1/workbench/program-roles/{roleName} and returns a flat
// RolePermissionsResponse ({ roleName, userType, permissions: string[] }),
// proxied by the BFF to the dealer-management system. Consumed via
// `useRolePermissions` (src/hooks/useRolePermissions.ts), which parses the flat
// list into per-section visible/writable/forcedReadOnly verdicts
// (src/lib/permissions/parseRolePermissions.ts). Role-only — deal status/phase
// and the VLA lock are combined in separately by `useWorkbenchPermissions`
// (src/hooks/useWorkbenchPermissions.ts), not a separate BFF endpoint.
export const programRoleCapabilitiesApi = wrapBFFClientWithAuth(new ProgramRoleCapabilitiesApi(config));

// Informed fraud verdict (SVRS-57313). getFraudVerdict(approvalId) hits GET
// /api/v1/workbench/applications/{approvalId}/fraud-verdict and returns a
// FraudVerdictResponse ({ fraudDetected, descriptions, recommendationsByType,
// documentFindings }), proxying chassis's Informed Defender lookup. A 404
// means chassis hasn't computed a verdict yet — treated as "not flagged" by
// the query hook, not surfaced as an error.
export const workbenchFraudVerdictApi = wrapBFFClientWithAuth(new FraudVerdictApi(config));
